// @ts-nocheck
import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {z} from "zod";
import {BaseAlert, BaseButton, BaseInput, BaseSelect, toast} from "@/components";
import {adapterBankList, findBankData} from "../../../adapters/bankListAdapter";
import usePagoMovilRegistration from "../../../hooks/usePagoMovilRegistration";
import usePutUserPagoMovilRegistrationData from "../../../hooks/usePutUserPagoMovilRegistrationData.hook";
import useUpdateProfile from "../../../hooks/useUpdateProfile";
import useCreateAccPagoMovil from "../../../hooks/wallet/useCreateAccPagoMovil";
import useEditAccPagoMovil from "../../../hooks/wallet/useEditAccPagoMovil";

const editInputPhoneCls = "h-12 rounded-full bg-transparent text-base";
const confirmationButtonsCls = "flex gap-3 justify-center flex-wrap";

const ID_PREFIX_OPTIONS = [
  {value: "V-", label: "V"},
  {value: "E-", label: "E"},
];

const simpleSchema = t =>
  z.object({
    bank: z.union([z.string(), z.number()]).refine(v => v !== "" && v !== null && v !== undefined, {
      message: t("mobilePaymentModalSelectBankMessage"),
    }),
    phone: z
      .string()
      .trim()
      .min(1, t("mobilePaymentModalPhonePlaceholderMessage"))
      .regex(/^\d+$/, "Only numbers are allowed")
      .min(10, t("phoneNumberMinLength") || "Phone number must have at least 10 digits")
      .max(11, t("phoneNumberMaxLength") || "Phone number cannot have more than 11 digits"),
  });

const fullSchema = t =>
  z.object({
    firstName: z
      .string()
      .trim()
      .min(1, t("nameRequired") || "Name is required"),
    lastName: z
      .string()
      .trim()
      .min(1, t("lastNameRequired") || "Last name is required"),
    idPrefix: z.enum(["V-", "E-"]),
    nationalID: z
      .string()
      .trim()
      .min(1, t("nationalIDRequired") || "ID is required")
      .regex(/^\d+$/, "Only numbers are allowed")
      .min(7, t("pagoMovilRegistrationCedulaMinLength") || "ID must have at least 7 digits")
      .max(15, t("pagoMovilRegistrationCedulaMaxLength") || "ID cannot have more than 15 digits"),
    phoneNumber: z
      .string()
      .trim()
      .min(1, t("phoneNumberRequired") || "Phone number is required")
      .regex(/^\d+$/, "Only numbers are allowed")
      .min(10, t("phoneNumberMinLength") || "Phone number must have at least 10 digits")
      .max(11, t("phoneNumberMaxLength") || "Phone number cannot have more than 11 digits"),
    bank: z.union([z.string(), z.number()]).refine(v => v !== "" && v !== null && v !== undefined, {
      message: t("mobilePaymentModalSelectBankMessage"),
    }),
  });

const updateSchema = t =>
  z.object({
    ID: z.any(),
    bank: z.union([z.string(), z.number()]).refine(v => v !== "" && v !== null && v !== undefined, {
      message: t("mobilePaymentModalSelectBankMessage"),
    }),
    phone: z
      .string()
      .trim()
      .min(1, t("mobilePaymentModalPhonePlaceholderMessage"))
      .regex(/^\d+$/, "Only numbers are allowed"),
  });

const AddPagoMovilForm = ({isUpdate, data, onSuccess}) => {
  const {t} = useTranslation();
  const {mutate, isPending, isSuccess} = useCreateAccPagoMovil();
  const {update, isPendingUpdate, isSuccessUpdate} = useEditAccPagoMovil();
  const {needsRegistration, userData, hasNationalID, hasPhoneNumber, executePendingAction} = usePagoMovilRegistration();

  const {
    putUserPagoMovilData,
    isPending: isPendingFullRegistration,
    isSuccess: isSuccessFullRegistration,
    error: errorFullRegistration,
  } = usePutUserPagoMovilRegistrationData();

  const {postUpdate, isPending: isPendingProfile, isSuccess: isSuccessProfile} = useUpdateProfile(() => {});

  const showFullRegistrationForm = !isUpdate && needsRegistration;
  const needsFullRegistration = !hasNationalID || !hasPhoneNumber;

  const [values, setValues] = useState({
    ID: "",
    firstName: "",
    lastName: "",
    idPrefix: "V-",
    nationalID: "",
    phoneNumber: "",
    bank: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});
  const [showConfirmOverlay, setShowConfirmOverlay] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState(null);
  // Keep reference to values used for pago-movil call after profile update
  const [queuedPagoMovil, setQueuedPagoMovil] = useState(null);

  useEffect(() => {
    if (isUpdate && data) {
      const bankData = findBankData({bank_code: data.bank_code});
      setValues(prev => ({
        ...prev,
        ID: data.ID,
        bank: data.bank_code,
        phone: String(data.phone_number ?? ""),
        _bankLabel: bankData?.bank_name || "",
      }));
    } else if (showFullRegistrationForm && userData) {
      setValues(prev => ({
        ...prev,
        firstName: userData.given_name || userData.firstName || "",
        lastName: userData.family_name || userData.lastName || "",
        nationalID: userData.nationalID || "",
        phoneNumber: userData.phoneNumber || "",
      }));
    }
  }, [isUpdate, data, showFullRegistrationForm, userData]);

  useEffect(() => {
    if (!errorFullRegistration) return;
    const errorMessage = errorFullRegistration.message || "";
    if (errorMessage.includes("duplicated.attribute.nationalID")) {
      setErrors(prev => ({...prev, nationalID: "Este número de cédula ya está registrado."}));
    } else if (errorMessage.includes("duplicated.attribute.phoneNumber")) {
      setErrors(prev => ({...prev, phoneNumber: "Este número de teléfono ya está registrado."}));
    } else {
      toast.error(
        `${t("pagoMovilRegistrationErrorTitle") || "Error"} — Hubo un error durante el registro. Por favor, inténtalo de nuevo.`
      );
    }
  }, [errorFullRegistration, t]);

  const setField = (field, value) => {
    setValues(v => ({...v, [field]: value}));
    if (errors[field]) setErrors(prev => ({...prev, [field]: undefined}));
  };
  const handleInput = field => e => setField(field, e.target.value);

  const validateAndExecute = () => {
    // biome-ignore lint/suspicious/noImplicitAnyLet: legacy
    let result;
    if (isUpdate) {
      result = updateSchema(t).safeParse(values);
    } else if (showFullRegistrationForm) {
      result = fullSchema(t).safeParse(values);
    } else {
      result = simpleSchema(t).safeParse(values);
    }
    if (!result.success) {
      const nextErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !nextErrors[key]) nextErrors[key] = issue.message;
      }
      setErrors(nextErrors);
      return null;
    }
    setErrors({});
    return result.data;
  };

  const executeSubmit = parsed => {
    try {
      const fullNationalID = parsed.idPrefix ? `${parsed.idPrefix}${parsed.nationalID}` : parsed.nationalID;

      if (showFullRegistrationForm) {
        const profileData = {
          firstName: parsed.firstName,
          lastName: parsed.lastName,
          nationalID: fullNationalID,
          phoneNumber: parsed.phoneNumber,
        };
        setQueuedPagoMovil({bank: parsed.bank, phone: parsed.phoneNumber});
        if (needsFullRegistration) {
          putUserPagoMovilData(profileData);
        } else {
          postUpdate(profileData);
        }
      } else if (isUpdate) {
        update({
          ID: parsed.ID,
          bank_code: Number(parsed.bank),
          phone_number: Number(parsed.phone),
        });
      } else {
        mutate({
          bank_code: Number(parsed.bank),
          phone_number: Number(parsed.phone),
        });
      }
    } catch {
      toast.error("Error — There was an error processing your request. Please try again.");
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    const parsed = validateAndExecute();
    if (!parsed) return;

    if (isUpdate) {
      setPendingFormValues(parsed);
      setShowConfirmOverlay(true);
      return;
    }
    executeSubmit(parsed);
  };

  const handleConfirmUpdate = () => {
    setShowConfirmOverlay(false);
    if (pendingFormValues) executeSubmit(pendingFormValues);
    setPendingFormValues(null);
  };

  const handleCancelUpdate = () => {
    setShowConfirmOverlay(false);
    setPendingFormValues(null);
  };

  // After profile update success → create Pago Móvil account
  useEffect(() => {
    if (!((isSuccessProfile || isSuccessFullRegistration) && showFullRegistrationForm && queuedPagoMovil)) {
      return;
    }
    mutate({
      bank_code: Number(queuedPagoMovil.bank),
      phone_number: Number(queuedPagoMovil.phone),
    });
    setQueuedPagoMovil(null);
  }, [isSuccessProfile, isSuccessFullRegistration, showFullRegistrationForm, queuedPagoMovil, mutate]);

  // Success notifications
  useEffect(() => {
    if (isSuccessUpdate) {
      toast.success("Pago movil actualizado correctamente");
      if (onSuccess) onSuccess();
      return;
    }
    if (isSuccess && !showFullRegistrationForm) {
      toast.success("Pago movil agregado correctamente");
      if (onSuccess) onSuccess();
      return;
    }
    if (isSuccess && showFullRegistrationForm) {
      toast.success(
        `${t("pagoMovilRegistrationSuccessTitle") || "Registration Successful"} — ${
          t("pagoMovilRegistrationSuccessMessage") ||
          "Your personal information and Pago Móvil account have been registered successfully."
        }`
      );
      if (onSuccess) onSuccess();
      const timeoutId = setTimeout(async () => {
        try {
          if (typeof executePendingAction === "function") {
            await executePendingAction();
          }
        } catch {
          // silent
        }
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [isSuccess, isSuccessUpdate, showFullRegistrationForm, t, onSuccess, executePendingAction]);

  const bankOptions = adapterBankList();
  const submitLoading = isUpdate ? isPendingUpdate : isPending || isPendingProfile || isPendingFullRegistration;

  return (
    <>
      <form
        onSubmit={handleSubmit}
        noValidate
      >
        {showFullRegistrationForm && (
          <div style={{marginBottom: "var(--space-5)"}}>
            <BaseAlert
              variant="warning"
              title={t("addPagoMovilExplanationTitle") || "Personal Registration Required"}
              description={t("addPagoMovilExplanationMessage")}
            />
          </div>
        )}

        {showFullRegistrationForm && (
          <>
            <div style={{marginBottom: "var(--space-4-5)"}}>
              <BaseInput
                label={t("name") || "Name"}
                value={values.firstName}
                onChange={handleInput("firstName")}
                error={errors.firstName}
              />
            </div>

            <div style={{marginBottom: "var(--space-4-5)"}}>
              <BaseInput
                label={t("lastName") || "Last Name"}
                value={values.lastName}
                onChange={handleInput("lastName")}
                error={errors.lastName}
              />
            </div>

            <div style={{marginBottom: "var(--space-4-5)"}}>
              <label style={{display: "block", marginBottom: "var(--space-2)", fontWeight: 500}}>
                {t("nationalID")}
              </label>
              <div style={{display: "flex", gap: "var(--space-2-5)"}}>
                <div style={{width: "var(--size-20)"}}>
                  <BaseSelect
                    value={values.idPrefix}
                    onChange={e => setField("idPrefix", e.target.value)}
                    options={ID_PREFIX_OPTIONS}
                  />
                </div>
                <div style={{flex: 1}}>
                  <BaseInput
                    value={values.nationalID}
                    onChange={handleInput("nationalID")}
                    error={errors.nationalID}
                  />
                </div>
              </div>
            </div>

            <div style={{marginBottom: "var(--space-4-5)"}}>
              <BaseInput
                label={t("phoneNumber") || "Phone Number"}
                value={values.phoneNumber}
                onChange={handleInput("phoneNumber")}
                error={errors.phoneNumber}
              />
            </div>
          </>
        )}

        <div style={{marginBottom: "var(--space-4-5)"}}>
          <BaseSelect
            label={
              <span>
                {t("mobilePaymentModalBank")}
                <span style={{color: "var(--color-danger-text)", marginLeft: "var(--space-1)"}}>*</span>
              </span>
            }
            placeholder={t("mobilePaymentModalSelectBank")}
            value={values.bank}
            onChange={e => setField("bank", e.target.value)}
            options={bankOptions}
            error={errors.bank}
            showSearch
          />
        </div>

        {!showFullRegistrationForm && (
          <div style={{marginBottom: "var(--space-4-5)"}}>
            <BaseInput
              label={
                <span>
                  {t("mobilePaymentModalPhone")}
                  <span style={{color: "var(--color-danger-text)", marginLeft: "var(--space-1)"}}>*</span>
                </span>
              }
              placeholder={t("mobilePaymentModalPhonePlaceholder")}
              value={values.phone}
              onChange={handleInput("phone")}
              error={errors.phone}
              containerClassName={editInputPhoneCls}
            />
          </div>
        )}

        <div style={{textAlign: "center", marginTop: "var(--space-5)"}}>
          <BaseButton
            type="submit"
            variant="primary"
            loading={submitLoading}
          >
            {showFullRegistrationForm
              ? t("registerAndAddPagoMovil") || "Register & Add Mobile Payment"
              : t("mobilePaymentModalSubmitBtn")}
          </BaseButton>
        </div>
      </form>

      {showConfirmOverlay && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "var(--color-alpha-black-20)",
            backdropFilter: "blur(5px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: "var(--z-toast)",
          }}
          onClick={handleCancelUpdate}
          onKeyDown={e => {
            if (e.key === "Escape") handleCancelUpdate();
          }}
          role="presentation"
        >
          <div
            style={{
              padding: "var(--space-5)",
              borderRadius: "var(--radius-md)",
              textAlign: "center",
              minWidth: 400,
            }}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            role="presentation"
          >
            <div className="confirmation-content">
              <span>{t("confirmSaveChanges") || "¿Estás seguro de que quieres guardar los cambios?"}</span>
              <div className={confirmationButtonsCls}>
                <BaseButton
                  variant="primary"
                  className="btn-tertiary-hover"
                  onClick={handleConfirmUpdate}
                  loading={isPendingUpdate}
                >
                  {t("accept") || "Aceptar"}
                </BaseButton>
                <BaseButton
                  variant="secondary"
                  className="btn-cancel-hover"
                  onClick={handleCancelUpdate}
                >
                  {t("cancel") || "Cancelar"}
                </BaseButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

AddPagoMovilForm.propTypes = {
  isUpdate: PropTypes.bool,
  data: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default AddPagoMovilForm;
